import moment from 'moment';

export function formatDate(date: Date): string {
  const year = date.getFullYear();
  let month = (date.getMonth() + 1) as string | number;
  let day = date.getDate() as string | number;

  if (day < 10) {
    day = `0${+day}`;
  }
  if (month < 10) {
    month = `0${+month}`;
  }

  return `${year}-${month}-${day}`;
}

export function isPendingWithinDayRange(date1: Date, date2: Date): boolean {
  const d1 = new Date(date1);
  const d2 = new Date(date2);
  const diff = Math.abs(d2.getTime() - d1.getTime());
  const diffInDays = Math.ceil(diff / (1000 * 3600 * 24));

  return diffInDays < 30;
}

export function formatSecondsToMinutes(number: number): string {
  const minutes = Math.floor(number / 60);
  let seconds = (number - minutes * 60) as string | number;
  if (seconds < 10) {
    seconds = `0${seconds}`;
  }
  return `${minutes}:${seconds}`;
}

export interface TimeLeft {
  days: number;
  hours: number;
}

/**
 * Determines how much time is left before a UTC timestamp
 * @param expirationDate {string} - A UTC timestamp
 * @return {TimeLeft} - A object containing days and hours remaining as numbers
 */
export const calculateTimeLeft = (expirationDate: string): TimeLeft => {
  const currentDate = moment.utc();
  const endDate = moment.utc(expirationDate);
  const duration = moment.duration(endDate.diff(currentDate));

  return {
    days: Math.floor(duration.as('days')),
    hours: Math.floor(duration.as('hours')),
  };
};

/**
 * Creates copy that can be used to tell a user how much time they have left in their subscription.
 * @param timeLeft {TimeLeft} - TimeLeft object containing days and hours remaining
 * in their subscription.
 * @return {String} - Copy to show user. e.g. "48 hours" or "3 days"
 */
export const getDurationCopy = ({ days, hours }: TimeLeft): string => {
  if (hours === 1) {
    return `${hours} hour`;
  }
  if (hours <= 48) {
    return `${hours} hours`;
  }
  // If less than 7 days, but greater than 48 hours left, show days rounded up
  if (hours < 168) {
    return `${days + 1} days`;
  }
  return `${days} days`;
};

export const getLocale = (): string => {
  if (navigator) {
    const { language } = navigator;
    return language;
  }
  return '';
};

export const getTimezone = (): string => Intl.DateTimeFormat().resolvedOptions().timeZone;

const dayInMS = 1000 * 60 * 60 * 24;

export const getDateNDaysAway = (date: Date, n: number): Date => {
  return new Date(date.getTime() + dayInMS * n);
};

export const datesAreOnSameDay = (firstDate: Date, secondDate: Date): boolean => {
  return (
    firstDate.getFullYear() === secondDate.getFullYear() &&
    firstDate.getMonth() === secondDate.getMonth() &&
    firstDate.getDate() === secondDate.getDate()
  );
};
